import React, { useCallback, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { first } from 'lodash';

import { beChargeCardOnFileV1 } from 'actions/payment/beChargeCardOnFile.action';
import beCreateTransaction from 'actions/payment/beCreateTransaction.action';
import feChangePaymentType from 'actions/payment/feChangePaymentType.action';
import { beFindReaderV3 } from 'actions/pos/beFindReader.action';
import beInitReaderPayment from 'actions/pos/beInitReaderPayment.action';
import feSetPosIntentId from 'actions/pos/feSetPosIntentId.action';
import updateProcedure from 'actions/scheduler/updateProcedure';
import ICO_FAILED from 'assets/images/dialogs/PaymentFailed.svg';
import IMG_CASH from 'assets/images/icons/cash-purple.svg';
import IMG_VOUCHER from 'assets/images/icons/gift-purple.svg';
import IMG_MONEY from 'assets/images/icons/money-purple.svg';
import IMG_PAY from 'assets/images/icons/pay-purple.png';
import IMG_STORE from 'assets/images/icons/store-purple.png';
import { LT_POS } from 'bl/loggers.bl';
import { chaosThrow, chaosTrue, performTx } from 'bl/payment/checkout.bl';
import { fromPropsToPaymentInfo, isPosFlowFinished, isPosFlowOpen } from 'bl/pos/checks.bl';
import determineAndStorePaymentIntentId from 'bl/pos/determineAndStorePaymentIntentId.bl';
import determinePriceLines from 'bl/pos/determinePriceLines.bl';
import DialogMessage from 'components/dialog/DialogMessage.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Space from 'components/generic/Space.component';
import PosReaderDialog from 'components/pos/PosReaderDialog.component';
import PRDS from 'components/pos/PosReaderDialogStatus.const';
import PosReaderPriceLine from 'components/pos/PosReaderPriceLine.component';
import PosStateMessage from 'components/pos/PosStateMessage.component';
import CurrencySign from 'components/price/CurrencySign.component';
import { USE_OLD_CARD_PAYMENT_ERROR } from 'config/use.conf';
import { CURRENCY_ACTIVE_BORDER_ICONS } from 'constants/currency.const';
import EMSG from 'constants/messages/errorMessage.const';
import PAYMENT_ORIGIN_POS from 'constants/payment/paymentOriginPos.const';
import POS_READER_STATUS from 'constants/pos/posReaderStatus.const';
import SF from 'constants/salonFlag.const';
import S from 'constants/string.const';
import { useCountryImage } from 'hooks/useCountryImage.hook';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { usePaymentAudit } from 'providers/PaymentAuditProvider.component';
import { useCurrentSalonId, useSalonFlags, useSingleSalonFlag } from 'providers/SalonBasicInfo.provider';
import { selectCashupVoucherList } from 'selectors/cashup.selectors';
import {
  selectCheckoutAppointments,
  selectCheckoutCardOnFileChargeSuccess,
  selectCheckoutChargingCardError,
  selectCheckoutClientId,
  selectCheckoutData,
  selectCheckoutFinishUrl,
  selectCheckoutPaymentOption,
  selectCheckoutPos,
  selectCheckoutSelectedAppointment,
} from 'selectors/checkout.selectors';
import { selectCurrentSalonId, selectCurrentSalonPosFlow } from 'selectors/currentSalon.selectors';
import { selectServiceMap } from 'selectors/salonInfo.selectors';
import alen from 'utils/arrays/alen.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { fromPropsToIsWeek, fromPropsToMatchedParams } from 'utils/converters/props/path.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { isEnvDev } from 'utils/env/determine.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import isNotEnumKey from 'utils/predicates/enum/isNotEnumKey.util';
import isPositiveNumber from 'utils/predicates/number/isPositiveNumber.util';
import { concatTag as ct } from 'utils/strings/manipulate.util';

import { changeAppointmentsStatus, updateAppointmentWithProcedures } from 'booking/actions';
import { getAllVoucher, getCashupVoucher } from 'cashup/actions';
import { cancelChargeSlickCardOnline, cleanTransactionData, useDeposit } from 'checkout/actions';
import AlertDialog from 'checkout/components/AlertDialog';
import ConfirmationDialogFromCheckout from 'checkout/components/ConfirmationDialog';
import PayPopUpPriceLine from 'checkout/components/PayPopUpPriceLine.component';
import { calculateCheckoutTaxesAndFees } from 'checkout/reducer';

import CN from './CheckoutPaymentPopUpV2.module.scss';

const CTAG = 'CheckoutPaymentPopUpV2';
const L = logger([CTAG, LT_POS]);

const IS_DEV = isEnvDev();

const USE_SENTRY_AUDIT_WHILE_CODING = true;

const CheckoutPaymentPopUpV2 = (props) => {
  const {
    pos,
    transaction,
    checkoutOption,
    cardOnFileChargeSuccess,
    beChargeCardOnFile,
    chargingCardError,
    appointmentDetails,
    data,
    procedures,
    posFlow,
    serviceMap,
    appointments: appointmentIds,
    clientId,
    appointment,
    finishUrl,
    history,
    feChangePaymentType,
    changeAppointmentsStatus,
    cleanTransactionData,
    feSetPosIntentId,
    beCreateTransaction,
    updateAppointmentWithProcedures,
    cancelChargeSlickCardOnline,
    beInitReaderPayment,
  } = props;

  const { t } = useTranslation();
  const { isSalonInUS } = useGilt();

  const salonId = useCurrentSalonId();
  const salonFlags = useSalonFlags(); // TODO: should no longer use the entire object for logging like in v1
  const isAuditCheckoutToSentryEnabled = useSingleSalonFlag(SF.auditCheckoutToSentry);
  const { val: isCheckoutPaymentPopUpV2open, nay: closeCheckoutPaymentPopUpV2 } = useTip(true);
  const { val: isTransitioningToPosLoading, yay: startPosTransitionLoading } = useTip(false);
  const { checkpoint, id: auditId } = usePaymentAudit({
    enabled: isAuditCheckoutToSentryEnabled || (USE_SENTRY_AUDIT_WHILE_CODING && IS_DEV),
  });
  useMemo(() => auditId && L.info('auditId:', auditId), [auditId]);

  const IMG_DEPOSIT = useCountryImage(CURRENCY_ACTIVE_BORDER_ICONS);

  // NOTE: this is only relevant in the callbacks (no components rendering), so just a reference to avoid re-creating them
  const paymentIntentIdRef = useRef('');

  // transaction initiated is only relevant for performTx in the callbacks, no components rendering needed
  const isTxInitiatedRef = useRef(false);

  // NOTE: isConfirmUpdateRendered for rendering components, doConfirmUpdateSet and isConfirmUpdateRef for the callbacks
  const [isConfirmUpdateRendered, doConfirmUpdateSet] = useState(false);
  const isConfirmUpdateRef = useRef(false);

  // NOTE: isCloseButtonDisabled for rendering components, doConfirmingSet and isConfirmingRef for the callbacks
  const [isCloseButtonDisabled, doConfirmingSet] = useState(false);
  const isConfirmingRef = useRef(false);

  // NOTE: isConfirmButtonDisabled for rendering components, doConfirmDisabledSet and isConfirmDisabledRef for the callbacks
  const [isConfirmButtonDisabled, doConfirmDisabledSet] = useState(false);
  const isConfirmDisabledRef = useRef(false);

  // NOTE: isMismatchDialogClosed for rendering components, setTotalsMatched and areTotalsMatchedRef for the callbacks
  const [isMismatchDialogClosed, setTotalsMatched] = useState(true);
  const areTotalsMatchedRef = useRef(true);

  const onlineAmountFromData = data?.onlineAmount;
  const cardAmountFromData = data?.cardAmount;
  const appointmentId = appointment?.id;
  const hasAppointmentIds = alen(appointmentIds) > 0;

  const { otherAmount, cardAmount, cardNumberEnd, depositAmount, cashAmount, onlineAmount, voucherAmount, total } = fromPropsToPaymentInfo(props);

  const addedProducts = asArray(data?.clients)?.flatMap((client) => client?.products ?? []);

  const checkoutBalance = calculateCheckoutTaxesAndFees({
    clients: data?.clients ?? [],
    services: props.services ?? [],
    products: addedProducts ?? [],
    taxes: props.taxes ?? [],
    fees: props.fees ?? [],
  });

  const totalInclTaxes = parseFloat(total + checkoutBalance.totalTaxesAndFeesAmount);
  const checkoutTotal = isSalonInUS ? totalInclTaxes : total;

  const priceLines = useMemo(() => determinePriceLines({ data, appointment, serviceMap, t }), [appointment, data, serviceMap, t]);
  // NOTE: Please do not adding these @type comments -  they do not add value and make the code harder to read.
  // If needed, put them on the line above instead.
  const [posStep, setPosStep] = /**@type UseState*/ useState();
  const [errorMessage, setErrorMessage] = useState('');

  const isFetching = fromAnyToBool(transaction?.isFetching);
  const hasDepositAmount = depositAmount > 0;

  const isPosEnabled = posFlow === true;
  const readersResult = usePrepareReadOnly(beFindReaderV3, { isMissingParams: !isPosEnabled });
  const isReadersLoading = readersResult?.isLoading;
  const readersRaw = useMemo(() => readersResult?.data?.originalJson?.results?.readers ?? [], [readersResult?.data?.originalJson?.results?.readers]);
  const readers = readersRaw.map((r) => ({
    ...r,
    isOnline: r.status === POS_READER_STATUS.online,
  }));

  const isHiddenStatus = (status) => status === POS_READER_STATUS.tapToPayTerminalNoStatus || status === POS_READER_STATUS.unavailable;

  const validReaders = readers.filter((r) => !isHiddenStatus(r.status));
  const hasValidOnlineReader = validReaders.some((r) => r.isOnline);
  const hasNoReader = readersRaw && !Array.isArray(readersRaw) && readersRaw?.generalReaderStatus === POS_READER_STATUS.noReader;

  const shouldUsePosFlow = isPosEnabled && hasValidOnlineReader;

  const shouldBlockPosFlow = isPosEnabled && !isReadersLoading && (hasNoReader || !hasValidOnlineReader);

  const isPosDialogOpen = !shouldBlockPosFlow && shouldUsePosFlow && isPosFlowOpen({ posFlow, cardAmount, posStep });
  const isInPersonDialogOpen = USE_OLD_CARD_PAYMENT_ERROR && chargingCardError && cardOnFileChargeSuccess === false;

  // NOTE: allows for more customized error message to take precedence
  // SEE: https://getslick.atlassian.net/browse/GS-10878
  const alertTitle = isInPersonDialogOpen ? S.empty : errorMessage || transaction?.errorMessage;

  const isWeek = fromPropsToIsWeek(props);
  const { stylistId } = fromPropsToMatchedParams(props);

  checkpoint('', {
    tag: 'render',
    salonId,
    clientId,
    isFetching,
    isPosDialogOpen,
    isConfirmUpdateRendered,
    isCloseButtonDisabled,
    isConfirmButtonDisabled,
    isMismatchDialogClosed,
    posStep,
    posFlow,
    priceLines,
    appointmentId,
    appointmentIds,
    hasAppointmentIds,
    hasDepositAmount,
    isWeek,
    stylistId,
    otherAmount,
    cardAmount,
    cardNumberEnd,
    depositAmount,
    cashAmount,
    onlineAmount,
    voucherAmount,
    total,
    errorMessage,
  });

  const goToCalendar = () => void navFor(CTAG, 'goToCalendar()', { history }).goat(-2);
  const confirm = useCallback(
    async (options) => {
      const FTAG = 'confirm()';
      const tag = ct(FTAG, options?.tag);

      {
        const isConfirmDisabled = isConfirmDisabledRef.current;
        if (isConfirmDisabled) {
          L.debug(FTAG, 'Aborting confirm because isConfirmDisabled:', isConfirmDisabled);
          const message = 'aborting charge due to positive disable flag';
          checkpoint('confirm.isConfirmDisabled.bail', { tag, isConfirmDisabled, message });
          return;
        }
      }

      {
        const tag = 'confirm';
        const value = true;
        isConfirmDisabledRef.current = value;
        doConfirmDisabledSet(value);
        checkpoint('isConfirmDisabled.yay', { tag, 'isConfirmDisabled.value': value });
      }
      checkpoint('confirm.disableConfirm.done', { tag, isConfirmDisabled: isConfirmDisabledRef.current });

      const { onlineAmount, clients } = data ?? {};
      const effectivePosFlow = 'posFlowOverride' in (options ?? {}) ? options.posFlowOverride : shouldUsePosFlow;

      const charges =
        checkoutBalance?.charges && isSalonInUS
          ? checkoutBalance?.charges
          : {
              taxes: [],
              fees: [],
              tax_exempts: {
                products: [],
                services: [],
              },
            };

      if (!isPositiveNumber(onlineAmount)) {
        const info = { tag, onlineAmount, appointmentIds, isConfirmDisabled: isConfirmDisabledRef.current };
        try {
          const message = 'creating transaction due to non-positive onlineAmount';
          checkpoint('confirm.noPositiveOnlineAmount.init', info, { message });
          const value = await performTx({
            tag: 'noOnlineAmount',
            checkpoint,
            paymentIntentIdRef,
            charges,
            areTotalsMatchedRef,
            setTotalsMatched,
            isTxInitiatedRef,
            onlineAmount,
            clients,
            isFetching,
            checkoutOption,
            cardAmountFromData,
            onlineAmountFromData,
            posFlow: effectivePosFlow,
            procedures,
            data,
            feChangePaymentType,
            appointmentIds,
            updateAppointmentWithProcedures,
            salonId,
            appointmentDetails,
            setErrorMessage,
            beCreateTransaction,
            hasAppointmentIds,
            changeAppointmentsStatus,
            finishUrl,
            isWeek,
            history,
            stylistId,
            t,
          });
          checkpoint('confirm.noPositiveOnlineAmount.pass', info, { value });
        } catch (error) {
          L.warn(FTAG, 'performTransaction error:', error);
          checkpoint('confirm.noPositiveOnlineAmount.error', info, { error });
        }
        return;
      }

      setErrorMessage(S.empty);

      const firstAppointmentId = first(appointmentIds);
      const firstClientId = first(clients).id ? fromAnyToInteger(first(clients).id) : 0;
      const isConfirmDisabled = isConfirmDisabledRef.current;
      const callInfo = { tag, firstAppointmentId, firstClientId, onlineAmount, isConfirmDisabled, appointmentIds };
      const payload = { salonId, clientId: firstClientId, appointmentId: firstAppointmentId, amount: onlineAmount };

      try {
        checkpoint('confirm.beChargeCardOnFile.init', callInfo, { payload });

        chaosThrow('throwBeforeConfirmChargeCardOnFile');
        const result = await beChargeCardOnFile(payload);

        const isResponseOk = result.is.ok;
        const responseChargeSuccess = result?.response?.success;
        const responsePaymentIntentId = result?.response?.intent_id;
        const httpStatusCode = result?.actualResponse?.status;

        const info = { tag, isResponseOk, result, httpStatusCode, responseChargeSuccess, responsePaymentIntentId };
        checkpoint('confirm.beChargeCardOnFile.pass', info);

        const isFailConfirmChargeCardOnFile = chaosTrue('failConfirmChargeCardOnFile');
        if (!isResponseOk || !responseChargeSuccess || isFailConfirmChargeCardOnFile) {
          L.warn(FTAG, 'charge result:', result, 'not setting paymentIntentId:', responsePaymentIntentId);

          const messageInfo = { payload, message: 'charge card on file failed', isFailConfirmChargeCardOnFile };
          checkpoint('confirm.beChargeCardOnFile.bail', callInfo, info, messageInfo);

          setErrorMessage(t(EMSG.txCreateTransKey));
          return;
        } // if no OK or no success

        L.debug(FTAG, 'chargeResult:', result, 'setting paymentIntentId:', responsePaymentIntentId);

        {
          const key = 'paymentIntentId.value';
          const prev = paymentIntentIdRef?.current;

          paymentIntentIdRef.current = fromAnyToString(responsePaymentIntentId);
          const value = paymentIntentIdRef.current;

          if (!value && prev) {
            const message = 'unset non-empty value';
            checkpoint('paymentIntentId.set.fail', { tag, [key]: value, message, responsePaymentIntentId });
            L.warn(FTAG, 'paymentIntentId', tag, message);
          } else if (!value) {
            const message = 'reset empty value with empty';
            checkpoint('paymentIntentId.set', { tag, [key]: value, message, responsePaymentIntentId });
            L.debug(FTAG, 'paymentIntentId.set', tag, message);
          } else {
            const message = 'set non-empty value';
            checkpoint('paymentIntentId.set', { tag, [key]: value, message, responsePaymentIntentId });
          }
        }

        checkpoint('confirm.awaitPerformTx.init', { tag, responsePaymentIntentId });

        const value = await performTx({
          tag: ct(tag, 'regular'),
          checkpoint,
          onlineAmount,
          clients,
          paymentIntentIdRef,
          charges,
          isTxInitiatedRef,
          isFetching,
          checkoutOption,
          cardAmountFromData,
          onlineAmountFromData,
          posFlow: shouldUsePosFlow,
          procedures,
          data,
          feChangePaymentType,
          appointmentIds,
          updateAppointmentWithProcedures,
          salonId,
          appointmentDetails,
          areTotalsMatchedRef,
          setTotalsMatched,
          setErrorMessage,
          beCreateTransaction,
          hasAppointmentIds,
          changeAppointmentsStatus,
          finishUrl,
          isWeek,
          history,
          stylistId,
          t,
        });

        const messageInfo = { value, message: 'charge card on file succeeded' };
        if (value) checkpoint('confirm.awaitPerformTx.pass', callInfo, info, messageInfo);
        else checkpoint('confirm.awaitPerformTx.fail', callInfo, info, messageInfo);
      } catch (error) {
        L.warn(FTAG, 'beChargeCardOnFile error:', error);
        checkpoint('confirm.awaitPerformTx.error', callInfo, { error, payload });
      }
    },
    [
      checkpoint,
      data,
      checkoutBalance?.charges,
      isSalonInUS,
      appointmentIds,
      salonId,
      isFetching,
      checkoutOption,
      cardAmountFromData,
      onlineAmountFromData,
      shouldUsePosFlow,
      procedures,
      feChangePaymentType,
      updateAppointmentWithProcedures,
      appointmentDetails,
      beCreateTransaction,
      hasAppointmentIds,
      changeAppointmentsStatus,
      finishUrl,
      isWeek,
      history,
      stylistId,
      t,
      beChargeCardOnFile,
    ],
  );

  const handleReaderInit = useCallback(() => {
    checkpoint('handleReaderInit.done', { tag: 'handleReaderInit', isConfirming: isConfirmingRef.current });

    {
      const tag = 'handleReaderInit > disableCloseButton';
      const value = true;
      isConfirmingRef.current = value;
      doConfirmingSet(value);
      checkpoint('isConfirming.yay', { tag, 'isConfirming.value': value });
    }
  }, [checkpoint]);

  const handleConfirmClick = useCallback(async () => {
    const tag = 'handleConfirmClick';
    try {
      const value = await confirm({ tag });
      checkpoint('handleConfirmClick.pass', { tag, value, isConfirming: isConfirmingRef.current });

      {
        const tag = 'handleConfirmClick > enableCloseButton';
        const value = false;
        isConfirmingRef.current = value;
        doConfirmingSet(value);
        checkpoint('isConfirming.nay', { tag, 'isConfirming.value': value });
      }
    } catch (error) {
      L.warn('handleConfirmClick()', error);
      checkpoint('handleConfirmClick.error', { tag, error });
    }
  }, [checkpoint, confirm, doConfirmingSet]);

  const handleConfirm = useCallback(async () => {
    startPosTransitionLoading();
    closeCheckoutPaymentPopUpV2();

    const FTAG = 'handleConfirm()';
    try {
      const { onlineAmount, cardAmount } = data ?? {};
      L.debug(FTAG, { posFlow, posStep, onlineAmount, cardAmount });
      checkpoint('handleConfirm.init', { tag: 'hc01', posFlow, posStep, onlineAmount, cardAmount });

      if (isPositiveNumber(onlineAmount)) {
        // TODO: check if dialog is supposed to be shown on positive or non-positive onlineAmount
        const tag = ct('handleConfirm.onlineAmount', 'showConfirmUpdateDialog');
        checkpoint('handleConfirm.onlineAmount.bail', { tag, onlineAmount });

        {
          const value = true;
          isConfirmUpdateRef.current = value;
          doConfirmUpdateSet(value);
          checkpoint('isConfirmUpdate.yay', { tag, 'isConfirmUpdate.value': value });
        }

        return;
      }

      // NOTE: If POS flow no valid reader/ not available,
      // we fallback to card payment flow and  skip POS checkout.
      if (isPositiveNumber(cardAmount)) {
        if (shouldBlockPosFlow) {
          checkpoint('pos.fallback.to.card', {
            hasValidOnlineReader,
            readersRaw,
            posFlow,
          });

          // exclude POS flow
          return confirm({ tag: 'fallback-card' });
        }

        const isFinished = isPosFlowFinished({ posFlow, posStep });

        if (isFinished) {
          try {
            checkpoint('handleConfirm.isFinished.init', { tag: 'hc03', isFinished, cardAmount });
            const value = await confirm({ tag: 'handleConfirm.isFinished' });
            checkpoint('handleConfirm.isFinished.pass', { tag: 'hc04', value, isFinished, cardAmount });
          } catch (error) {
            L.warn(FTAG, error);
            checkpoint('handleConfirm.isFinished.error', { tag: 'hc05', error, isFinished, cardAmount });
          }

          {
            const tag = 'hc06';
            const isConfirming = isConfirmingRef.current;
            checkpoint('handleConfirm.isFinished.done', { tag, isFinished, cardAmount, isConfirming });
          }

          {
            const tag = 'handleConfirm.isFinished > enableCloseButton';
            const value = false;
            isConfirmingRef.current = value;
            doConfirmingSet(value);
            checkpoint('isConfirming.nay', { tag, 'isConfirming.value': value });
          }
          return;
        } // isFinished

        if (isNotEnumKey(PRDS, posStep) || PRDS.closed === posStep) {
          L.debug(FTAG, 'posStep:', posStep, '->', PRDS.open);
          checkpoint('handleConfirm.isNotFinished.bail', { tag: 'open', newStep: PRDS.open, posStep, isFinished });

          setPosStep(PRDS.open);

          return;
        }
      } // isPositiveNumber(cardAmount)

      try {
        checkpoint('handleConfirm.regular.init', { tag: 'hc07', cardAmount, onlineAmount });
        const value = await confirm({ tag: 'handleConfirm.regular' });
        checkpoint('handleConfirm.regular.pass', { tag: 'hc08', value, cardAmount, onlineAmount });
      } catch (error) {
        L.warn(FTAG, error);
        checkpoint('handleConfirm.regular.error', { tag: 'hc09', error, cardAmount, onlineAmount });
      }

      checkpoint('handleConfirm.done', { tag: 'hc10', isConfirming: isConfirmingRef.current });

      {
        const tag = 'handleConfirm.done > enableCloseButton';
        const value = false;
        isConfirmingRef.current = value;
        doConfirmingSet(value);
        checkpoint('isConfirming.nay', { tag, 'isConfirming.value': value });
      }
    } catch (error) {
      const { onlineAmount, cardAmount } = data ?? {};
      checkpoint('handleConfirm.error', { tag: 'hc11', error, onlineAmount, cardAmount });
    }
  }, [startPosTransitionLoading, closeCheckoutPaymentPopUpV2, data, posFlow, posStep, checkpoint, shouldBlockPosFlow, hasValidOnlineReader, readersRaw, confirm]);

  const closePopup = useCallback(() => {
    if (isFetching) {
      L.debug('closePopup()', 'Not closing due to isFetching:', isFetching);
    } else {
      navFor(CTAG, 'closePopup()', { isFetching }, { history }).back();
    }
  }, [history, isFetching]);

  const handleAlertClose = useCallback(() => {
    setErrorMessage(S.empty);
    cleanTransactionData();
    closePopup();
  }, [cleanTransactionData, closePopup]);

  const handleCancel = useCallback(
    (ev) => {
      const tag = fromAnyToTrimmed(ev?.tag);
      closePopup();
      if (onlineAmountFromData > 0 || cardAmountFromData > 0) {
        cancelChargeSlickCardOnline();
        checkpoint('handleCancel.cancelChargeSlickCardOnline.done', { tag, onlineAmountFromData, cardAmountFromData });
      } else {
        checkpoint('handleCancel.noAmount.done', { tag, onlineAmountFromData, cardAmountFromData });
      }
    },
    [cancelChargeSlickCardOnline, cardAmountFromData, checkpoint, closePopup, onlineAmountFromData],
  );

  const handlePosReaderDone = useCallback(
    async (ev) => {
      const { status, canceled, success, isNonSlick, paymentIntentId: idFromEvent } = ev ?? {};

      const info = { tag: 'A', status, canceled, success, isNonSlick, idFromEvent, idFromState: paymentIntentIdRef.current };
      L.debug('handlePosReaderDone()', info, ev);

      if (canceled && success) {
        checkpoint('handlePosReaderDone.conflict.warn', info, { message: 'both success and cancel are truthy' });
      } else {
        checkpoint('handlePosReaderDone.noConflict.done', info);
      }

      if (canceled) handleCancel({ tag: 'handlePosReaderDone' });

      if (isNonSlick) {
        try {
          checkpoint('handlePosReaderDone.nonSlick.init', { tag: 'E' });
          const value = await confirm({ tag: 'handlePosReaderDone.nonSlick', posFlowOverride: false });
          checkpoint('handlePosReaderDone.nonSlick.pass', { tag: 'E', value });
        } catch (error) {
          checkpoint('handlePosReaderDone.nonSlick.error', { tag: 'E', error });
        }
      } else if (success) {
        try {
          checkpoint('handlePosReaderDone.confirm.init', { tag: 'B', success });
          const value = await confirm({ tag: 'handlePosReaderDone' });
          checkpoint('handlePosReaderDone.confirm.pass', { tag: 'B', success, value });
        } catch (error) {
          checkpoint('handlePosReaderDone.confirm.error', { tag: 'B', success, error });
        }
      } else {
        checkpoint('handlePosReaderDone.notSuccess.done', { tag: 'C', success });
      }

      checkpoint('handlePosReaderDone.done', { tag: 'D', success, isConfirming: isConfirmingRef.current });
      {
        const tag = 'handlePosReaderDone > enableCloseButton';
        const value = false;
        isConfirmingRef.current = value;
        doConfirmingSet(value);
        checkpoint('isConfirming.nay', { tag, 'isConfirming.value': value });
      }
    },
    [paymentIntentIdRef, handleCancel, checkpoint, confirm, doConfirmingSet],
  );

  const handleCancelPayment = useCallback(() => {
    const tag = ct('handleCancelPayment', 'hideConfirmUpdateDialog');

    {
      const message = 'confirm payment canceled due to user action';
      const isConfirmUpdateDialogRendered = isConfirmUpdateRef.current;
      checkpoint('handleCancelPayment.done', { tag, message, isConfirmUpdateDialogRendered });
    }

    {
      const value = false;
      isConfirmUpdateRef.current = value;
      doConfirmUpdateSet(value);
      checkpoint('isConfirmUpdate.nay', { tag, 'isConfirmUpdate.value': value });
    }
  }, [checkpoint, doConfirmUpdateSet]);

  const handlePosReaderAction = useCallback(
    async (ev) => {
      const FTAG = 'handlePosReaderAction()';

      const items = isSalonInUS
        ? [
            ...priceLines,
            {
              description: t('checkout.paymentPopUp.priceLineTaxesAndFees'),
              amount: checkoutBalance.totalTaxesAndFeesAmount,
              quantity: 1,
            },
          ]
        : priceLines;

      const totalPriceOfServices = priceLines.reduce((acc, { serviceId, finalPrice }) => acc + (serviceId ? finalPrice : 0), 0);

      const options = {
        appointmentId,
        amount: cardAmountFromData,
        amountBeforeTax: totalPriceOfServices,
        beInitReaderPayment,
        clientId,
        feSetPosIntentId,
        flags: salonFlags,
        items,
        paymentOrigin: PAYMENT_ORIGIN_POS.checkout,
        pos,
        readerId: ev?.readerId,
      };

      try {
        checkpoint('handlePosReaderAction.init', { tag: 'init', ev, options });

        chaosThrow('throwBeforeDetermineIntentId');
        const promise = determineAndStorePaymentIntentId(options);

        const result = await promise;
        L.debug(FTAG, 'result:', result);

        const isFailDetermineIntentId = chaosTrue('failDetermineIntentId');
        const determinedPaymentIntentId = isFailDetermineIntentId ? void 1 : result?.paymentIntentId;

        checkpoint('handlePosReaderAction.pass', {
          tag: 'pass',
          ev,
          options,
          result,
          determinedPaymentIntentId,
          isFailDetermineIntentId,
        });

        {
          const tag = 'handlePosReaderAction';
          const key = 'paymentIntentId.value';
          const prev = paymentIntentIdRef?.current;

          paymentIntentIdRef.current = fromAnyToString(determinedPaymentIntentId);
          const value = paymentIntentIdRef.current;

          if (!value && prev) {
            const message = 'unset non-empty value';
            checkpoint('paymentIntentId.set.fail', { tag, [key]: value, message, isFailDetermineIntentId });
            L.warn(FTAG, 'paymentIntentId', tag, message);
          } else if (!value) {
            const message = 'reset empty value with empty';
            checkpoint('paymentIntentId.set', { tag, [key]: value, message, isFailDetermineIntentId });
            L.debug(FTAG, 'paymentIntentId.set', tag, message);
          } else {
            const message = 'set non-empty value';
            checkpoint('paymentIntentId.set', { tag, [key]: value, message, isFailDetermineIntentId });
          }
        }

        return promise;
      } catch (error) {
        L.warn(FTAG, error);
        checkpoint('handlePosReaderAction.error', { tag: 'error', ev, options, error });
      }
    },
    [appointmentId, beInitReaderPayment, cardAmountFromData, checkoutBalance.totalTaxesAndFeesAmount, checkpoint, clientId, feSetPosIntentId, isSalonInUS, pos, priceLines, salonFlags, t],
  );

  const handlePosReaderChange = useCallback(
    (ev) => {
      L.debug(`handlePosReaderChange()`, ev?.status);
      checkpoint('handlePosReaderChange.done', { tag: 'handlePosReaderChange', ev });
      setPosStep(ev?.status);
    },
    [checkpoint],
  );

  return (
    <div data-bem="CheckoutPaymentPopUpV2">
      {fromAnyToBool(alertTitle) && <AlertDialog title={alertTitle} buttonText={t('common.buttons.close')} onClick={handleAlertClose} onClose={handleAlertClose} />}

      <PosReaderDialog price={cardAmount} open={isPosDialogOpen} onAction={handlePosReaderAction} onChange={handlePosReaderChange} onDone={handlePosReaderDone} onInit={handleReaderInit}>
        <DialogMessage>
          <PosStateMessage posStep={posStep} price={cardAmount} />
        </DialogMessage>

        {priceLines.map(({ finalPrice, clientId, productId, serviceId, ...extra }) => (
          <PosReaderPriceLine {...extra} total={finalPrice} key={`${clientId}-${serviceId}-${productId}`} />
        ))}

        <PosReaderPriceLine description={t('composite.posReaderDialog.total')} total={cardAmount} />
      </PosReaderDialog>

      {isInPersonDialogOpen && (
        <ConfirmationDialogFromCheckout onRequestClose={handleCancel} title=" " leftButtonText="" rightButtonText={t('common.buttons.ok')} onRight={handleCancel} displayHeader="header-non">
          <div className="dialog-inner-title">{t('checkout.paymentPopUp.confirmationModalTitle')}</div>
          <img src={ICO_FAILED} alt="" className="dialog-image" />
          <div className="no-show-message center">{t(EMSG.chargeInPersonTransKey)}</div>
        </ConfirmationDialogFromCheckout>
      )}

      {isConfirmUpdateRendered && (
        <SalonModal tag="CheckoutPaymentPopUpV2__bill" isOpen>
          <SalonModal.Body>
            <div className={CN.modalImage}>
              <div className={CN.iconContainer}>
                <Icon variant="pay" selected width="lg" />
              </div>
            </div>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>
                <Space>{t('composite.posDepositDialog.chargePart1')}</Space>
                <span className={CN.textFocused}>
                  <Space>
                    <CurrencySign value={onlineAmount} />
                  </Space>
                </span>
                <Space>{t('composite.posDepositDialog.chargePart3')}</Space>

                <span className={CN.textFocused}>
                  <Space>{t('composite.posDepositDialog.cardEnding')}</Space>
                  <Space>{fromAnyToTrimmed(cardNumberEnd)}</Space>
                </span>
              </div>

              <div className={CN.modalText}>
                {t('composite.posDepositDialog.takePaymentDescription.part1')}
                {S.space}
                <span className={CN.warningText}>{t('composite.posDepositDialog.takePaymentDescription.part2')}</span>
                {S.space}
                {t('composite.posDepositDialog.takePaymentDescription.part3')}
              </div>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalFooter}>
              <Button variant="outlineForce" onClick={handleCancelPayment}>
                <span className={CN.buttonSpace}>{t('common.buttons.cancelUpp')}</span>
              </Button>

              <Button variant="primaryForce" onClick={handleConfirmClick} disabled={isConfirmButtonDisabled}>
                <span className={CN.buttonSpace}>{t('common.buttons.confirmUpp')}</span>
              </Button>
            </div>
          </SalonModal.Footer>
        </SalonModal>
      )}

      {!isMismatchDialogClosed && (
        <ConfirmationDialogFromCheckout onRequestClose={goToCalendar} title=" " leftButtonText="" rightButtonText={t('common.buttons.ok')} onRight={goToCalendar} displayHeader="header-non">
          <div className="dialog-inner-title">{t('checkout.paymentPopUp.fromCheckoutModal.title')}</div>
          <img src={ICO_FAILED} alt="Client paid" className="dialog-image" />
          <div className="no-show-message center">{t('checkout.paymentPopUp.fromCheckoutModal.description')}</div>
        </ConfirmationDialogFromCheckout>
      )}

      {isTransitioningToPosLoading && <OverlaySpinner />}

      <SalonModal tag="CheckoutPaymentPopUpV2__bill" isOpen={isCheckoutPaymentPopUpV2open}>
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button tag="CheckoutPaymentPopUpV2__close" variant="empty" onClick={handleCancel} disabled={isCloseButtonDisabled}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalImage}>
            <div className={CN.iconContainer}>
              <Icon variant="pay" selected width="lg" />
            </div>
          </div>
          <div className={CN.modalContent}>
            <div className={CN.modalTitle}>
              {t('checkout.paymentPopUp.title')}
              <div className={CN.textFocused}>
                <Space>
                  <CurrencySign value={checkoutTotal} />
                </Space>
              </div>
            </div>

            <div data-bem="CheckoutPaymentPopUpV2__body" className={CN.modalText}>
              <PayPopUpPriceLine description={t('checkout.paymentPopUp.priceLineCardEnding', { cardNumberEnd })} value={onlineAmount} icon={IMG_PAY} />
              <PayPopUpPriceLine description={t('checkout.paymentPopUp.priceLineCardInStore')} value={cardAmount} icon={IMG_STORE} />
              <PayPopUpPriceLine description={t('checkout.paymentPopUp.priceLineCash')} value={cashAmount} icon={IMG_CASH} />
              <PayPopUpPriceLine description={t('checkout.paymentPopUp.priceLineVoucher')} value={voucherAmount} icon={IMG_VOUCHER} />
              <PayPopUpPriceLine description={t('checkout.paymentPopUp.priceLineOther')} value={otherAmount} icon={IMG_MONEY} />
              {hasDepositAmount && <PayPopUpPriceLine description={t('checkout.paymentPopUp.priceLineDepositRedeemed')} value={depositAmount} icon={IMG_DEPOSIT} />}
            </div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div data-bem="CheckoutPaymentPopUpV2__buttons">
            {posStep === PRDS.success || posStep === PRDS.closed || isFetching ? (
              <Button tag="CheckoutPaymentPopUpV2__button-loading" variant="primaryForce">
                <span className={CN.buttonSpace}>{t('common.loading')}</span>
              </Button>
            ) : (
              <Button tag="CheckoutPaymentPopUpV2__button-confirm" variant="primaryForce" onClick={handleConfirm}>
                <span className={CN.buttonSpace}>{t('common.buttons.confirmUpp')}</span>
              </Button>
            )}
          </div>
        </SalonModal.Footer>
      </SalonModal>
    </div>
  );
};

const CheckoutPaymentPopUpV2Hoc = connect(
  (state) => ({
    salonId: selectCurrentSalonId(state),
    posFlow: selectCurrentSalonPosFlow(state),
    clientId: selectCheckoutClientId(state),
    data: selectCheckoutData(state),
    appointment: selectCheckoutSelectedAppointment(state),
    pos: selectCheckoutPos(state),
    serviceMap: selectServiceMap(state),
    services: asArray(state.currentSalon.services).reduce((registered, { services }) => registered.concat(services), []),
    checkoutOption: selectCheckoutPaymentOption(state),

    finishUrl: selectCheckoutFinishUrl(state),
    appointments: selectCheckoutAppointments(state),
    chargingCardError: selectCheckoutChargingCardError(state),
    cardOnFileChargeSuccess: selectCheckoutCardOnFileChargeSuccess(state),
    voucherList: selectCashupVoucherList(state),
    procedures: state.scheduler.procedures,
    appointmentDetails: state.scheduler.appointmentDetails,
    transaction: state.transaction.create,
  }),
  mapActionsToDispatchToProps({
    beCreateTransaction,
    cleanTransactionData,
    changeAppointmentsStatus,
    feChangePaymentType,
    useDeposit,
    updateAppointmentWithProcedures,
    updateProcedure,
    beChargeCardOnFile: beChargeCardOnFileV1,
    getAllVoucher,
    getCashupVoucher,
    cancelChargeSlickCardOnline,
    beInitReaderPayment,
    feSetPosIntentId,
  }),
)(CheckoutPaymentPopUpV2);

export default CheckoutPaymentPopUpV2Hoc;
